import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  BarChart3, Settings, 
  LogOut, Menu, X, User as UserIcon, TrendingUp, TrendingDown, CalendarDays, Sun, Moon, ShieldCheck
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';

const TopbarItem: React.FC<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = ({ to, icon, label, onClick }) => {
  return (
    <NavLink 
      to={to} 
      onClick={onClick}
      className={({ isActive }) => 
        `flex items-center gap-2 px-3.5 py-2 rounded-xl transition-all duration-200 font-semibold text-xs tracking-wide uppercase ${
          isActive 
            ? 'bg-white/25 dark:bg-brand-500/25 text-white dark:text-brand-200 shadow-md shadow-purple-950/20 backdrop-blur-md scale-105' 
            : 'text-purple-100 dark:text-slate-300 hover:bg-white/10 dark:hover:bg-slate-800 hover:text-white dark:hover:text-white active:scale-95'
        }`
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className="hidden lg:inline">{label}</span>
    </NavLink>
  );
};

const MobileSidebarItem: React.FC<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = ({ to, icon, label, onClick }) => {
  return (
    <NavLink 
      to={to} 
      onClick={onClick}
      className={({ isActive }) => 
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm ${
          isActive 
            ? 'bg-white/20 dark:bg-brand-500/20 text-white dark:text-brand-300 shadow-sm border-l-4 border-white dark:border-brand-400 pl-3' 
            : 'text-purple-100 dark:text-slate-400 hover:bg-white/10 dark:hover:bg-slate-800 hover:text-white dark:hover:text-slate-200 active:scale-98'
        }`
      }
    >
      {icon}
      <span className="tracking-wide">{label}</span>
    </NavLink>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Default to light mode if no theme is saved
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const headerText = localStorage.getItem('app_header_text') || 'Gestão Fácil';
  const headerSubtitle = localStorage.getItem('app_header_subtitle') || 'Painel Web';
  const logoUrl = localStorage.getItem('report_logo_url');

  const pageTitle = () => {
    switch (location.pathname) {
      case '/dashboard': return 'Despesas & Lançamentos';
      case '/revenues': return 'Receitas & Entradas';
      case '/agenda': return 'Agenda & Calendário Financeiro';
      case '/reports': return 'Relatórios Financeiros';
      case '/settings': return 'Configurações do Sistema';
      case '/profile': return 'Meu Perfil';
      case '/users': return 'Gestão de Usuários';
      case '/quotes': return 'Cotações & Orçamentos';
      default: return 'Painel Financeiro';
    }
  };

  return (
    <div className="flex flex-col h-screen print:h-auto bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 overflow-hidden print:overflow-visible font-sans transition-colors duration-300">
      
      {/* Top Navigation Bar - Modern Glassmorphism */}
      <header className="h-16 bg-gradient-to-r from-purple-800 via-purple-900 to-indigo-900 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 backdrop-blur-xl border-b border-purple-700/40 dark:border-slate-800 grid grid-cols-[auto_1fr_auto] items-center px-4 lg:px-8 shadow-lg shadow-purple-950/10 z-20 sticky top-0 transition-colors duration-300 print:hidden gap-4">
        <div className="flex items-center gap-3">
           <button 
             onClick={() => setIsMobileMenuOpen(true)} 
             className="md:hidden p-2 text-white dark:text-slate-300 hover:text-purple-200 dark:hover:text-white hover:bg-white/10 rounded-xl transition-all active:scale-95"
             aria-label="Abrir Menu"
           >
              <Menu size={22} />
           </button>
           
           {/* Nova Logo com Ícone, Nome do Sistema e Link para a Dashboard de Despesas */}
           <BrandLogo size="md" linkTo="/dashboard" theme="glass" />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex justify-center">
          <nav className="flex items-center gap-1.5 bg-black/15 dark:bg-white/5 p-1 rounded-2xl border border-white/10 dark:border-white/5">
            <TopbarItem to="/dashboard" icon={<TrendingDown size={16} />} label="Despesas" />
            <TopbarItem to="/revenues" icon={<TrendingUp size={16} />} label="Receitas" />
            <TopbarItem to="/agenda" icon={<CalendarDays size={16} />} label="Agenda" />
            <TopbarItem to="/reports" icon={<BarChart3 size={16} />} label="Relatórios" />
            <TopbarItem to="/settings" icon={<Settings size={16} />} label="Configurações" />
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 justify-end">
           {/* Dark Mode Toggle */}
           <button 
             onClick={toggleDarkMode} 
             className="p-2 text-purple-100 dark:text-slate-300 hover:bg-white/10 dark:hover:bg-slate-800 rounded-xl transition-all active:scale-90"
             title={isDarkMode ? "Mudar para modo claro" : "Mudar para modo escuro"}
             aria-label="Alternar Tema"
           >
             {isDarkMode ? <Sun size={19} className="text-amber-300" /> : <Moon size={19} />}
           </button>

           <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-purple-600/50 dark:border-slate-800">
              <NavLink to="/profile" className="flex items-center gap-2.5 p-1 hover:bg-white/10 dark:hover:bg-slate-800 rounded-xl transition-all group">
                 <div className="w-8 h-8 rounded-full bg-purple-900 dark:bg-slate-800 flex items-center justify-center overflow-hidden border-2 border-white/30 dark:border-slate-700 shadow-sm group-hover:scale-105 transition-transform">
                    {user?.photoUrl ? (
                      <img src={user.photoUrl} alt="User" className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon className="text-white dark:text-slate-300" size={16} />
                    )}
                 </div>
                 <div className="hidden lg:block text-left">
                    <p className="text-white font-bold text-xs truncate max-w-[110px] leading-tight">{user?.name}</p>
                    <span className="text-[10px] text-purple-200 dark:text-slate-400 font-medium uppercase tracking-wider">{user?.role || 'Admin'}</span>
                 </div>
              </NavLink>
              <button 
                onClick={handleLogout} 
                className="p-2 text-purple-100 hover:text-red-300 dark:text-slate-400 dark:hover:text-red-400 hover:bg-white/10 dark:hover:bg-slate-800 rounded-xl transition-all active:scale-95" 
                title="Sair do Sistema"
                aria-label="Sair"
              >
                 <LogOut size={18} />
              </button>
           </div>
        </div>
      </header>

      {/* Mobile Sidebar (Overlay with Smooth Animation) */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex animate-fade-in">
           <div className="w-72 bg-gradient-to-b from-purple-900 to-indigo-950 dark:from-slate-900 dark:to-slate-950 backdrop-blur-2xl h-full shadow-2xl flex flex-col border-r border-purple-800/50 dark:border-slate-800 animate-slide-up">
               <div className="h-16 flex items-center justify-between px-5 border-b border-white/10 dark:border-slate-800">
                  <BrandLogo size="sm" linkTo="/dashboard" theme="glass" onClick={() => setIsMobileMenuOpen(false)} />
                  <button 
                    onClick={() => setIsMobileMenuOpen(false)} 
                    className="p-1.5 text-purple-200 dark:text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                    aria-label="Fechar Menu"
                  >
                    <X size={22}/>
                  </button>
               </div>

              <div className="flex-1 py-4 px-3 space-y-2 overflow-y-auto">
                <div>
                   <p className="px-4 text-[10px] font-extrabold text-purple-300/80 dark:text-slate-500 uppercase tracking-widest mb-2">Financeiro</p>
                   <div className="space-y-1">
                     <MobileSidebarItem to="/dashboard" icon={<TrendingDown size={18} />} label="Despesas" onClick={() => setIsMobileMenuOpen(false)}/>
                     <MobileSidebarItem to="/revenues" icon={<TrendingUp size={18} />} label="Receitas" onClick={() => setIsMobileMenuOpen(false)}/>
                     <MobileSidebarItem to="/agenda" icon={<CalendarDays size={18} />} label="Agenda" onClick={() => setIsMobileMenuOpen(false)}/>
                     <MobileSidebarItem to="/reports" icon={<BarChart3 size={18} />} label="Relatórios" onClick={() => setIsMobileMenuOpen(false)}/>
                   </div>
                </div>

                <div className="pt-3">
                   <p className="px-4 text-[10px] font-extrabold text-purple-300/80 dark:text-slate-500 uppercase tracking-widest mb-2">Administração</p>
                   <div className="space-y-1">
                     <MobileSidebarItem to="/settings" icon={<Settings size={18} />} label="Configurações" onClick={() => setIsMobileMenuOpen(false)}/>
                     <MobileSidebarItem to="/profile" icon={<UserIcon size={18} />} label="Meu Perfil" onClick={() => setIsMobileMenuOpen(false)}/>
                   </div>
                </div>
              </div>

              <div className="p-4 border-t border-white/10 dark:border-slate-800">
                <button 
                  onClick={handleLogout} 
                  className="flex items-center justify-center gap-2 text-white bg-red-600/80 hover:bg-red-600 text-sm font-bold w-full py-3 rounded-xl shadow-lg transition-all active:scale-95"
                >
                  <LogOut size={16} /> Sair do Sistema
                </button>
              </div>
           </div>
           <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden print:overflow-visible p-4 sm:p-6 lg:p-8 relative flex flex-col">
         {/* Page Title for Mobile */}
         <div className="md:hidden mb-4 animate-fade-in">
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">{pageTitle()}</h2>
         </div>
         
         {/* Page Transition Wrapper */}
         <div key={location.pathname} className="max-w-7xl w-full mx-auto flex-1 flex flex-col relative z-10 animate-fade-in animate-slide-up">
            {children}
         </div>
         
         {/* Required System Footer */}
         <footer className="mt-auto pt-8 pb-4 text-center text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200/80 dark:border-slate-800/80 transition-colors print:hidden">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 font-medium">
              <span>2026 Gestão Fácil ERP ® • Todos os direitos reservados.</span>
              <span className="hidden sm:inline text-slate-300 dark:text-slate-600">•</span>
              <span>
                Desenvolvido por{" "}
                <a 
                  href="http://alonedev.com/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="font-bold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline transition-colors"
                >
                  AloneDev.com ®
                </a>{" "}
                • Todos os direitos reservados.
              </span>
            </div>
         </footer>
         
         {/* Decorative Background Glow Elements */}
         <div className="fixed top-20 left-10 w-80 h-80 bg-brand-500/5 dark:bg-brand-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
         <div className="fixed bottom-20 right-10 w-96 h-96 bg-blue-500/5 dark:bg-blue-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
      </main>
    </div>
  );
};
